import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  InputAdornment,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import {
  Add,
  Refresh,
  Close,
  Search,
  CardGiftcard,
  Stars,
  Star,
  StarBorder,
  Edit,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListRewardsQuery,
  useAddRewardMutation,
  useUpdateRewardMutation,
  useSetDefaultRewardMutation,
  Reward,
  RewardFormData,
  validateAmount,
  validatePoints,
} from '../../features/admin/rewardApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
  gold: '#ffd700',
};

const RewardManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Filter states
  const [searchStr, setSearchStr] = useState('');

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [formData, setFormData] = useState<RewardFormData>({
    points: '',
    amount: '',
    is_default: false,
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListRewardsQuery();

  const [addReward, { isLoading: isAdding }] = useAddRewardMutation();
  const [updateReward, { isLoading: isUpdating }] = useUpdateRewardMutation();
  const [setDefaultReward, { isLoading: isSettingDefault }] = useSetDefaultRewardMutation();

  // Handle API response structure
  const rewards = data?.data || [];
  const defaultReward = Array.isArray(rewards)
    ? rewards.find((reward: Reward) => reward.is_default === 1)
    : undefined;

  const isFormLoading = isAdding || isUpdating;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      points: '',
      amount: '',
      is_default: false,
    });
    setFormErrors({});
    setEditingReward(null);
  };

  const handleAddReward = () => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      is_default: !defaultReward
    }));
    setFormOpen(true);
  };

  // ✅ FIXED: Convert API number values to strings
  const handleEditReward = (reward: Reward) => {
    setEditingReward(reward);
    setFormData({
      id: reward.id,
      points: String(reward.points),
      amount: String(reward.amount),
      is_default: reward.is_default === 1,
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const handleSetDefault = async (reward: Reward) => {
    try {
      const result = await setDefaultReward({ id: reward.id }).unwrap();
      setSnackbar({
        open: true,
        message: result?.message || `Reward is now the default!`,
        severity: 'success'
      });
      refetch();
    } catch (err: any) {
      console.error('Failed to set default reward:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Failed to set default reward. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  // ✅ FIXED: Convert to string before calling .trim()
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    const amountStr = String(formData.amount).trim();
    const pointsStr = String(formData.points).trim();

    if (!amountStr) {
      newErrors.amount = 'Amount is required';
    } else if (!validateAmount(amountStr)) {
      newErrors.amount = 'Invalid amount (must be a positive number)';
    } else if (parseFloat(amountStr) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (!pointsStr) {
      newErrors.points = 'Points are required';
    } else if (!validatePoints(pointsStr)) {
      newErrors.points = 'Invalid points (must be a positive number)';
    } else if (parseFloat(pointsStr) <= 0) {
      newErrors.points = 'Points must be greater than 0';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ FIXED: Convert to string before calling .trim()
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        id: editingReward?.id,
        points: String(formData.points).trim(),
        amount: String(formData.amount).trim(),
        is_default: formData.is_default,
      };

      let result;
      if (editingReward) {
        result = await updateReward(submitData).unwrap();
      } else {
        result = await addReward(submitData).unwrap();
      }

      setSnackbar({
        open: true,
        message: result?.message || `Reward conversion ${editingReward ? 'updated' : 'created'} successfully!`,
        severity: 'success'
      });
      handleFormClose();
      refetch();
    } catch (err: any) {
      console.error('Failed to save reward:', err);

      let errorMessage = `Error ${editingReward ? 'updating' : 'creating'} reward conversion. Please try again.`;

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view rewards.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No rewards found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load rewards. Please try again.';
      }
    }
    return 'Network error. Please check connection.';
  };

  // ✅ FIXED: Safe array check before filter
  const filteredRewards = Array.isArray(rewards)
    ? rewards.filter((reward: Reward) => {
        const matchesSearch = !searchStr ||
          String(reward.amount).includes(searchStr) ||
          String(reward.points).includes(searchStr);
        return matchesSearch;
      })
    : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{
                  fontWeight: 700,
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Reward Conversions
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Manage reward points and conversion rates {defaultReward && (
                    <Chip
                      label={`Default: ${defaultReward.amount} AED = ${defaultReward.points} points`}
                      size="small"
                      icon={<Star sx={{ fontSize: 16 }} />}
                      sx={{
                        ml: 1,
                        backgroundColor: shopifyColors.gold + '20',
                        color: shopifyColors.textPrimary,
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddReward}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Add New Conversion
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search by amount or points..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ flexGrow: 1 }} />

                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredRewards.length} conversion{filteredRewards.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading conversions...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredRewards.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <CardGiftcard sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No conversions found' : 'No reward conversions yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr
                    ? `No reward conversions match your search "${searchStr}"`
                    : 'Add your first reward conversion to get started'
                  }
                </Typography>
                {!searchStr && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddReward}
                    sx={{
                      backgroundColor: shopifyColors.primary,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: shopifyColors.primaryLight,
                      },
                    }}
                  >
                    Add New Conversion
                  </Button>
                )}
              </Card>
            )}

            {/* Rewards List */}
            {!isLoading && filteredRewards.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredRewards.map((reward: Reward, index: number) => (
                    <ListItem
                      key={reward.id}
                      sx={{
                        borderBottom: index < filteredRewards.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                        position: 'relative',
                      }}
                    >
                      {reward.is_default === 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: '4px',
                            backgroundColor: shopifyColors.gold,
                          }}
                        />
                      )}

                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: reward.is_default === 1 ? shopifyColors.gold : shopifyColors.primaryLight,
                            width: 48,
                            height: 48,
                          }}
                        >
                          {reward.is_default === 1 ? <Star /> : <CardGiftcard />}
                        </Avatar>
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                              {parseFloat(String(reward.amount)).toFixed(2)} AED = {reward.points} Points
                            </Typography>
                            {reward.is_default === 1 && (
                              <Chip
                                label="Default"
                                size="small"
                                icon={<Star sx={{ fontSize: 14 }} />}
                                sx={{
                                  backgroundColor: shopifyColors.gold + '20',
                                  color: shopifyColors.textPrimary,
                                  fontWeight: 500,
                                  height: '22px',
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Amount: AED {parseFloat(String(reward.amount)).toFixed(2)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Stars sx={{ fontSize: 16, color: reward.is_default === 1 ? shopifyColors.gold : shopifyColors.purple }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Points: {reward.points}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, mt: 0.5, display: 'block' }}>
                              ID: {reward.id} • Created: {new Date(reward.created_at || '').toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Reward">
                          <IconButton
                            onClick={() => handleEditReward(reward)}
                            size="small"
                            sx={{
                              color: shopifyColors.textSecondary,
                              '&:hover': { color: shopifyColors.blue }
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>

                        {reward.is_default !== 1 && (
                          <Tooltip title="Set as Default">
                            <IconButton
                              onClick={() => handleSetDefault(reward)}
                              disabled={isSettingDefault}
                              size="small"
                              sx={{
                                color: shopifyColors.textSecondary,
                                '&:hover': { color: shopifyColors.gold }
                              }}
                            >
                              <StarBorder />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleFormClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {editingReward ? 'Edit Reward Conversion' : 'Add Reward Conversion'}
            </Typography>
            <IconButton
              onClick={handleFormClose}
              size="small"
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{
                  color: shopifyColors.textPrimary,
                  fontWeight: 600,
                  mb: 2
                }}>
                  Conversion Rate
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="amount"
                      label="Amount (AED)"
                      value={formData.amount}
                      onChange={handleInputChange}
                      error={!!formErrors.amount}
                      helperText={formErrors.amount || "Monetary value in AED"}
                      fullWidth
                      required
                      placeholder="1.00"
                      disabled={isFormLoading}
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">AED</InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="points"
                      label="Points"
                      value={formData.points}
                      onChange={handleInputChange}
                      error={!!formErrors.points}
                      helperText={formErrors.points || "Points equivalent"}
                      fullWidth
                      required
                      placeholder="100"
                      disabled={isFormLoading}
                      type="number"
                      inputProps={{ step: "1", min: "0" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Stars sx={{ fontSize: 20, color: shopifyColors.gold }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, mt: 1, display: 'block' }}>
                  💡 Conversion: AED {formData.amount || '0'} = {formData.points || '0'} points
                </Typography>
              </Box>

              <Divider />

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_default"
                      checked={formData.is_default || false}
                      onChange={handleSwitchChange}
                      disabled={isFormLoading}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                        Set as default reward conversion
                      </Typography>
                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                        {defaultReward && formData.is_default && (!editingReward || editingReward.id !== defaultReward.id)
                          ? `⚠️ This will replace the current default (AED ${defaultReward.amount} = ${defaultReward.points} points)`
                          : 'This will be the default conversion rate used throughout the system'
                        }
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{
            p: 3,
            pt: 2,
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
             <Button
              onClick={handleFormClose}
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: '#eeeeee',
                bgcolor: '#f44336',
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleFormClose}
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: '#eeeeee',
                bgcolor: '#F78E12',
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormLoading || !String(formData.amount).trim() || !String(formData.points).trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': { backgroundColor: shopifyColors.primaryLight },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {editingReward ? 'Updating...' : 'Adding...'}
                </Box>
              ) : (
                editingReward ? 'Update Conversion' : 'Add Conversion'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RewardManagement;